import React from 'react';
import type { ViewerToolConfig } from '../../../server/admin/viewer-config/types';

interface ToolsConfigTabProps {
  value: ViewerToolConfig[];
  onChange: (updated: ViewerToolConfig[]) => void;
}

function generateId(): string {
  return crypto.randomUUID ? crypto.randomUUID() : `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function ToolsConfigTab({ value, onChange }: ToolsConfigTabProps) {
  const addTool = () => {
    onChange([
      ...value,
      { id: generateId(), name: '', imageUrl: null, description: '', order: value.length },
    ]);
  };

  const updateTool = (index: number, field: keyof ViewerToolConfig, val: string | null) => {
    const updated = [...value];
    updated[index] = { ...updated[index], [field]: val };
    onChange(updated);
  };

  const removeTool = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const moveTool = (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= value.length) return;
    const updated = [...value];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    onChange(updated.map((t, i) => ({ ...t, order: i })));
  };

  return (
    <div>
      <div className="vc-list">
        {value.map((tool, index) => (
          <div key={tool.id} className="vc-list-item">
            <div className="vc-list-item-header">
              <div className="vc-list-item-drag" title="Reorder">
                <button
                  type="button"
                  onClick={() => moveTool(index, -1)}
                  disabled={index === 0}
                  style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: '0 0.125rem', opacity: index === 0 ? 0.3 : 1 }}
                >
                  ▲
                </button>
                <button
                  type="button"
                  onClick={() => moveTool(index, 1)}
                  disabled={index === value.length - 1}
                  style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: '0 0.125rem', opacity: index === value.length - 1 ? 0.3 : 1 }}
                >
                  ▼
                </button>
              </div>
              <span style={{ flex: 1, fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>
                #{index + 1}
              </span>
              <button
                type="button"
                className="vc-list-item-delete"
                onClick={() => removeTool(index)}
                title="Remove tool"
              >
                ✕
              </button>
            </div>
            <div className="vc-list-item-fields">
              <input
                className="vc-input"
                type="text"
                value={tool.name}
                onChange={(e) => updateTool(index, 'name', e.target.value)}
                placeholder="Tool name (required)"
              />
              <input
                className="vc-input"
                type="text"
                value={tool.description || ''}
                onChange={(e) => updateTool(index, 'description', e.target.value)}
                placeholder="Description (optional)"
              />
              <input
                className="vc-input"
                type="url"
                value={tool.imageUrl || ''}
                onChange={(e) => updateTool(index, 'imageUrl', e.target.value || null)}
                placeholder="Image URL (optional)"
              />
            </div>
          </div>
        ))}
      </div>

      <button type="button" className="vc-add-btn" onClick={addTool}>
        + Add Tool
      </button>
    </div>
  );
}
